import React, { useContext, useMemo } from "react";
import { Navigate, Outlet } from "react-router-dom";

import Loading from "ui/Loading";
import { useLoadServerIfNeeded } from "utils/useLoadServerIfNeeded";
import { useRouteTopLevelReference } from "utils/useRouteTopLevelReference";
import { useUserPreferences } from "utils/hooks";

import {
  useWithCurrentOrganizationQuery,
  WithCurrentOrganizationQuery,
} from "./gql/Query.generated";

type CurrentUserType = Omit<
  WithCurrentOrganizationQuery["getCurrentUserDetails"],
  "__typename"
>;

type CurrentOrganizationType = Omit<
  CurrentUserType["organizations"][number],
  "__typename"
> | null;

export type CurrentServerType = Omit<
  CurrentUserType["organizations"][number]["servers"][number],
  "__typename"
> | null;

export type CurrentServerGroupType = Omit<
  CurrentUserType["organizations"][number]["serverGroups"][number],
  "__typename"
> | null;

type ContextValue =
  | [
      CurrentUserType,
      CurrentOrganizationType | undefined,
      CurrentServerType | undefined,
      CurrentServerGroupType | undefined,
    ]
  | undefined;

export const CurrentOrganizationContext =
  React.createContext<ContextValue>(undefined);

const WithCurrentOrganization = ({
  children,
}: {
  children: React.ReactNode;
}) => {
  const { loading, data, error } = useWithCurrentOrganizationQuery();

  const {
    organizationSlug: organizationSlugFromRoute,
    serverId: serverIdFromRoute,
    serverGroupId,
    databaseId,
  } = useRouteTopLevelReference();
  const { serverId, serverIdLoaded } = useLoadServerIfNeeded(
    serverIdFromRoute,
    databaseId,
  );

  const ctxValue = useMemo<ContextValue>(() => {
    if (!serverIdLoaded || loading || error || !data) {
      return undefined;
    }

    const { organizations } = data.getCurrentUserDetails;

    let organizationSlug = organizationSlugFromRoute;
    if (!organizationSlug && serverId) {
      for (const o of organizations) {
        if (o.servers.findIndex((s) => s.humanId === serverId) !== -1) {
          organizationSlug = o.slug;
          break;
        }
        if (
          o.serverGroups.some((sg) =>
            sg.servers.some((s) => s.humanId === serverId),
          )
        ) {
          organizationSlug = o.slug;
          break;
        }
      }
    }
    if (!organizationSlug && serverGroupId) {
      for (const o of organizations) {
        if (o.serverGroups.findIndex((sg) => sg.id === serverGroupId) !== -1) {
          organizationSlug = o.slug;
          break;
        }
      }
    }

    const currentOrganization = organizationSlug
      ? organizations.find((o) => o.slug === organizationSlug)
      : null;

    const currentServer =
      currentOrganization && serverId
        ? currentOrganization.servers.find((s) => s.humanId === serverId) ??
          currentOrganization.serverGroups
            .flatMap((sg) => sg.servers)
            .find((s) => s.humanId === serverId) ??
          null
        : null;

    const currentServerGroup =
      currentOrganization && serverGroupId
        ? currentOrganization.serverGroups.find(
            (sg) => sg.id === serverGroupId,
          ) ?? null
        : null;

    return [
      data.getCurrentUserDetails,
      currentOrganization,
      currentServer,
      currentServerGroup,
    ];
  }, [
    data,
    organizationSlugFromRoute,
    serverId,
    serverIdLoaded,
    serverGroupId,
    loading,
    error,
  ]);

  if (ctxValue == undefined) {
    return <Loading error={!!error} />;
  }

  return (
    <CurrentOrganizationContext.Provider value={ctxValue}>
      {children}
    </CurrentOrganizationContext.Provider>
  );
};

export function useCurrentOrganization(
  optional = false,
): CurrentOrganizationType | undefined {
  const ctx = useContext(CurrentOrganizationContext);
  if (!ctx) {
    throw new Error(
      "useCurrentOrganization(): must have ancestor WithCurrentOrganization component",
    );
  }
  const [, currentOrganization] = ctx;
  if (!currentOrganization && !optional) {
    throw new Error(
      "useCurrentOrganization(): current organization required but none set",
    );
  }

  return currentOrganization;
}

export function useCurrentServer(
  optional = false,
): CurrentServerType | undefined {
  const ctx = useContext(CurrentOrganizationContext);
  if (!ctx) {
    throw new Error(
      "useCurrentServer(): must have ancestor WithCurrentOrganization component",
    );
  }
  const [, , currentServer] = ctx;
  if (!currentServer && !optional) {
    throw new Error("useCurrentServer(): current server required but none set");
  }

  return currentServer;
}

export function useCurrentServerGroup(
  optional = false,
): CurrentServerGroupType | undefined {
  const ctx = useContext(CurrentOrganizationContext);
  if (!ctx) {
    throw new Error(
      "useCurrentServerGroup(): must have ancestor WithCurrentOrganization component",
    );
  }
  const [, , , currentServerGroup] = ctx;
  if (!currentServerGroup && !optional) {
    throw new Error(
      "useCurrentServerGroup(): current server group required but none set",
    );
  }

  return currentServerGroup;
}

// The cluster primary belongs to the server's cluster, which the server group exposes. A server that
// is not in a cluster (or whose cluster primary the user cannot see) stands in for itself.
export function useClusterPrimaryServerHumanId(
  server: CurrentServerType | undefined,
): string | undefined {
  const currentOrganization = useCurrentOrganization(true);
  if (!server) {
    return undefined;
  }
  const containingGroup = currentOrganization?.serverGroups.find((sg) =>
    sg.servers.some((s) => s.humanId === server.humanId),
  );

  return containingGroup?.primaryServerHumanId ?? server.humanId;
}

export function useCurrentUser(): CurrentUserType {
  const ctx = useContext(CurrentOrganizationContext);
  if (!ctx) {
    throw new Error(
      "useCurrentUser(): must have ancestor WithCurrentOrganization component",
    );
  }
  const [currentUser] = ctx;
  return currentUser;
}

export function useFallbackServerId(): string | undefined {
  const [serverSelect] = useUserPreferences("serverSelect");
  const currentOrganization = useCurrentOrganization(true);
  const organizationSlug = currentOrganization?.slug;
  const recentServerIds = useMemo(() => {
    if (!organizationSlug) {
      return [];
    }
    const items = (serverSelect.recentItems || {})[organizationSlug] || [];
    return items.filter((i) => i.type === "server").map((i) => i.id);
  }, [serverSelect.recentItems, organizationSlug]);

  if (!currentOrganization) {
    return undefined;
  }

  const servers = [...currentOrganization.servers].sort((a, b) => {
    if (
      recentServerIds.includes(a.humanId) &&
      recentServerIds.includes(b.humanId)
    ) {
      return (
        recentServerIds.indexOf(a.humanId) - recentServerIds.indexOf(b.humanId)
      );
    }
    if (recentServerIds.includes(a.humanId)) {
      return -1;
    }
    if (recentServerIds.includes(b.humanId)) {
      return 1;
    }
    if (a.hasRecentSnapshot && !b.hasRecentSnapshot) {
      return -1;
    }
    if (!a.hasRecentSnapshot && b.hasRecentSnapshot) {
      return 1;
    }
    return 0;
  });
  return servers[0]?.humanId;
}

export const WithCurrentServerGroup = () => {
  const currentServerGroup = useCurrentServerGroup(true);
  if (!currentServerGroup) {
    return <Navigate to="/" replace />;
  }
  return <Outlet />;
};

export default WithCurrentOrganization;
